import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { invalidateApiCache } from "../api/cache";
import { createAuthClient } from "../../features/auth/api/auth-client";

interface AuthContextValue {
  session: any;
  user: any;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isLoading: true,
  isAuthenticated: false,
  signOut: async () => {},
  refreshSession: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const authClient = createAuthClient();

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await authClient.getSession();
      if (data?.user) {
        setSession(data);
      } else {
        // Fallback: check cookie directly
        const hasCookie = document.cookie.includes("better-auth.session_token");
        if (hasCookie && !data?.user) {
          // Cookie exists but getSession failed — try to get user info from a protected endpoint
          try {
            const res = await fetch("/api/auth/session", {
              credentials: "include",
            });
            const sessionData = await res.json();
            if (sessionData?.user) {
              setSession(sessionData);
              return;
            }
          } catch {
            // ignore
          }
        }
        setSession(null);
      }
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check session once on mount; route changes should not refetch auth state.
  useEffect(() => {
    setIsLoading(true);
    refreshSession();
  }, [refreshSession]);

  const signOut = async () => {
    try {
      await authClient.signOut();
    } catch {
      // ignore
    }
    // Also clear the cookie manually
    document.cookie = "better-auth.session_token=; Max-Age=0; Path=/";
    invalidateApiCache();
    setSession(null);
  };

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    isLoading,
    isAuthenticated: !!session?.user,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
